import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';

function ChipIcon({ size = 48, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <circle cx="32" cy="32" r="30" fill="url(#chipGrad1)" stroke="#F59E0B" strokeWidth="2"/>
      <circle cx="32" cy="32" r="24" fill="url(#chipGrad2)" stroke="#FCD34D" strokeWidth="1.5"/>
      <circle cx="32" cy="32" r="16" fill="url(#chipGrad3)" stroke="#F59E0B" strokeWidth="1"/>
      <rect x="30" y="2" width="4" height="8" rx="2" fill="#FCD34D"/>
      <rect x="30" y="54" width="4" height="8" rx="2" fill="#FCD34D"/>
      <rect x="2" y="30" width="8" height="4" rx="2" fill="#FCD34D"/>
      <rect x="54" y="30" width="8" height="4" rx="2" fill="#FCD34D"/>
      <rect x="10.5" y="8.5" width="4" height="8" rx="2" fill="#FCD34D" transform="rotate(-45 10.5 8.5)"/>
      <rect x="45.5" y="47.5" width="4" height="8" rx="2" fill="#FCD34D" transform="rotate(-45 45.5 47.5)"/>
      <rect x="8.5" y="45.5" width="4" height="8" rx="2" fill="#FCD34D" transform="rotate(45 8.5 45.5)"/>
      <rect x="47.5" y="10.5" width="4" height="8" rx="2" fill="#FCD34D" transform="rotate(45 47.5 10.5)"/>
      <text x="32" y="37" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#FCD34D" fontFamily="Arial">M</text>
      <defs>
        <radialGradient id="chipGrad1" cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#D97706"/>
          <stop offset="100%" stopColor="#92400E"/>
        </radialGradient>
        <radialGradient id="chipGrad2" cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#B45309"/>
          <stop offset="100%" stopColor="#78350F"/>
        </radialGradient>
        <radialGradient id="chipGrad3" cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#F59E0B"/>
          <stop offset="100%" stopColor="#D97706"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

interface ChipPackage {
  id: number;
  price: number;
  websiteChips: string;
  inGameChips: string | null;
  bonusPct: number | null;
  noBonus: boolean;
  badge: string | null;
  highlight: boolean;
}

export default function ChipsShopPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 游戏内优惠：20M无优惠, 50M无优惠, 100M+20%=120M, 200M+150%=500M, 500M+200%=1500M, 1000M+160%=2600M, 2000M+210%=6200M, 3000M+220%=9600M
  // 官网再+20%：无优惠档不变，有优惠档×1.2
  const chipsPackages: ChipPackage[] = [
    { id: 1, price: 1.99,   websiteChips: '20M',    inGameChips: null,    bonusPct: null, noBonus: true,  badge: 'Starter',   highlight: false },
    { id: 2, price: 4.99,   websiteChips: '50M',    inGameChips: null,    bonusPct: null, noBonus: true,  badge: null,        highlight: false },
    { id: 3, price: 9.99,   websiteChips: '144M',   inGameChips: '120M',  bonusPct: 44,   noBonus: false, badge: null,        highlight: false },
    { id: 4, price: 19.99,  websiteChips: '600M',   inGameChips: '500M',  bonusPct: 200,  noBonus: false, badge: 'Popular',   highlight: true  },
    { id: 5, price: 49.99,  websiteChips: '1800M',  inGameChips: '1500M', bonusPct: 260,  noBonus: false, badge: 'Best Value', highlight: true  },
    { id: 6, price: 99.99,  websiteChips: '3120M',  inGameChips: '2600M', bonusPct: 212,  noBonus: false, badge: null,        highlight: false },
    { id: 7, price: 199.99, websiteChips: '7440M',  inGameChips: '6200M', bonusPct: 272,  noBonus: false, badge: null,        highlight: false },
    { id: 8, price: 299.99, websiteChips: '11520M', inGameChips: '9600M', bonusPct: 284,  noBonus: false, badge: 'Max Bonus', highlight: true  },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-0 pb-0 overflow-hidden" style={{ minHeight: '400px' }}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=casino%20poker%20chips%20stacked%20in%20dramatic%20golden%20light%2C%20multiple%20colorful%20chip%20towers%20on%20dark%20green%20felt%20table%2C%20cinematic%20moody%20lighting%2C%20rich%20amber%20glow%2C%20luxury%20gambling%20atmosphere%2C%20close%20up%20detailed%20shot%2C%20photorealistic%20render%2C%20dark%20background&width=1440&height=400&seq=chips-hero-bg-002&orientation=landscape"
            alt="Chips Store"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-zinc-950"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 pt-36 pb-16">
          <button
            onClick={() => navigate('/shop')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-black/40 border border-zinc-700 rounded-lg hover:border-amber-500/60 transition-colors mb-8 cursor-pointer whitespace-nowrap backdrop-blur-sm"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            <span className="text-sm">Back to Shop</span>
          </button>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/40 rounded-full mb-5 backdrop-blur-sm">
              <i className="ri-vip-crown-fill text-amber-400 text-sm"></i>
              <span className="text-sm font-semibold text-amber-300">Chips Store</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black mb-4 bg-gradient-to-r from-amber-300 via-yellow-400 to-orange-400 bg-clip-text text-transparent">
              Purchase Chips
            </h1>
            <p className="text-base text-zinc-300 max-w-xl mx-auto">
              Exclusive website discount · Get approximately <strong className="text-amber-400">20%</strong> more chips than in-game purchases
            </p>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {chipsPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative bg-zinc-900 border rounded-2xl p-5 hover:shadow-xl hover:shadow-amber-500/15 transition-all duration-300 cursor-pointer group hover:-translate-y-1 ${
                  pkg.highlight ? 'border-amber-500/60 shadow-md shadow-amber-500/10' : 'border-zinc-800 hover:border-amber-500/40'
                }`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg z-10">
                    <span className="text-xs font-bold text-white whitespace-nowrap">{pkg.badge}</span>
                  </div>
                )}

                {/* Price */}
                <div className="text-center mb-4 pt-2">
                  <div className="text-3xl font-black text-white">${pkg.price}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">USD</div>
                </div>

                {/* Chip Icon + Amount */}
                <div className="text-center mb-4 py-5 bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-xl border border-amber-500/20 group-hover:border-amber-500/40 transition-colors">
                  <div className="flex items-center justify-center mb-2">
                    <div className="group-hover:scale-110 transition-transform duration-300 drop-shadow-lg">
                      <ChipIcon size={52} />
                    </div>
                  </div>
                  <div className="text-2xl font-black text-amber-400 tracking-wide">{pkg.websiteChips}</div>
                  <div className="text-xs text-zinc-400 mt-1">Chips</div>
                </div>

                {/* Comparison or No Bonus */}
                {pkg.noBonus ? (
                  <div className="mb-4 p-3 bg-zinc-800/60 rounded-xl text-center">
                    <span className="text-xs text-zinc-400">Same as in-game</span>
                    <div className="text-xs text-zinc-500 mt-0.5">No extra bonus</div>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-zinc-800/60 rounded-xl space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400">In-game</span>
                      <span className="text-zinc-300 line-through">{pkg.inGameChips}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400">Website</span>
                      <span className="text-amber-400 font-bold">{pkg.websiteChips}</span>
                    </div>
                  </div>
                )}

                {/* Bonus Badge */}
                <div className="mb-4 text-center">
                  {pkg.noBonus ? (
                    <div className="inline-flex items-center gap-1 px-3 py-1 bg-zinc-700/50 border border-zinc-600/50 rounded-full">
                      <span className="text-xs text-zinc-400">No extra bonus</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/15 border border-green-500/30 rounded-full">
                      <i className="ri-arrow-up-line text-green-400 text-xs"></i>
                      <span className="text-xs font-bold text-green-400">+{pkg.bonusPct}% Bonus</span>
                    </div>
                  )}
                </div>

                {/* Buy Button */}
                <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl font-bold text-white group-hover:shadow-lg group-hover:shadow-amber-500/30 transition-all duration-300 whitespace-nowrap text-sm">
                  <span>Buy Now</span>
                  <i className="ri-shopping-cart-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 px-4 bg-zinc-900/40 border-t border-zinc-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: 'Secure Payment', sub: 'Secure Payment Protection' },
              { icon: 'ri-flashlight-line', title: 'Instant Delivery', sub: 'Instant Delivery' },
              { icon: 'ri-customer-service-2-line', title: '24/7 Support', sub: '24/7 Customer Support' },
            ].map((f) => (
              <div key={f.title} className="text-center p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
                <div className="w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <i className={`${f.icon} text-xl text-amber-400`}></i>
                </div>
                <h3 className="text-base font-bold mb-1">{f.title}</h3>
                <p className="text-sm text-zinc-400">{f.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Shop</h4>
              <ul className="space-y-2">
                <li><a href="/shop/chips" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Chips Store</a></li>
                <li><a href="/shop/coins" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Gold Coins Store</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Support</h4>
              <ul className="space-y-2">
                <li><a href="https://t.me/AmagiPoker" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/privacy" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="/license" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">License Agreement</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Download</h4>
              <div className="flex gap-3">
                <a href="https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-amber-400 transition-colors cursor-pointer" title="App Store">
                  <i className="ri-apple-fill text-lg"></i>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-amber-400 transition-colors cursor-pointer" title="Google Play">
                  <i className="ri-google-play-fill text-lg"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-800 text-center">
            <p className="text-sm text-zinc-500">© 2025 Amagi Poker. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
